import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useRef, useState } from 'react';
import CaseHeader from 'components/organisms/case-header/CaseHeader';
import CitiesNavigation from 'components/molecules/cases-cities-navigation/CitiesNavigation';
import NextCase from 'components/organisms/next-case/NextCase';
import styles from './Case.module.scss';
import type { CaseProps } from './Case.data';

import DynamicComponent from '../../DynamicComponent';
import { useGlobalStore } from '../../../stores/global';
import type { NextCaseBlokData } from '../../organisms/next-case/NextCase.data';
import { isCrossFlow } from '../../../pages/[[...slug]].config';
import { StorySlug } from '../../../util/storyblok/types/api.types';
import useScrollTrigger from '../../../hooks/useScrollTrigger';
import useIsomorphicLayoutEffect from '../../../hooks/useIsomorphicLayoutEffect';
import { useRouter } from 'next/router';
import LottieView from '../../atoms/lottie-view/LottieView';

export default function Case({ blok, canTransitionIn, ref, ...props }: CaseProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const { modules, ...caseData } = blok;
  const store = useGlobalStore();
  const router = useRouter();
  const { nextStory } = store;

  const [revealNextCase, setRevealNextCase] = useState(false);
  const [isAllowed, setIsAllowed] = useState(true);

  useIsomorphicLayoutEffect(() => {
    const { countryCode } = store;

    if (!countryCode) return;
    let allowed = true;

    const geoRedirect = async () => {
      if (caseData.countries?.length > 0) {
        if (caseData.exclude_countries) {
          allowed = !caseData.countries?.includes(countryCode);
        } else {
          allowed = caseData.countries?.includes(countryCode);
        }
      }

      if (!allowed) {
        setIsAllowed(false);
        await router.push('/work');
      }
    };

    geoRedirect();
  }, [store.countryCode]);

  useScrollTrigger({
    trigger: elementRef,
    vars: {
      scrub: true,
      start: 'bottom-=10% bottom',
      end: 'bottom bottom',
      onEnter: () => {
        setRevealNextCase(true);
      },
    },
  });

  return (
    <>
      <section
        ref={elementRef}
        className={classNames(styles.case, props.className)}
        {...sbEditable(blok)}
      >
        <CaseHeader
          blok={caseData}
          skipTransitionIn={isCrossFlow(store.toPath, store.fromPath)}
          canTransitionIn={canTransitionIn}
        />

        {!isAllowed && (
          <div className={styles.fullHeightContainer}>
            <div className={classNames(styles.screenHeightContainer)}>
              <div className={classNames(styles.lottieHolder)}>
                <LottieView
                  id="loader-dc"
                  loop
                  autoplay
                  pauseWhenOutViewport={false}
                  className={classNames(styles.lottieIcon, 'abs-fill')}
                />
              </div>
            </div>
          </div>
        )}

        {modules?.map((item) => (
          <DynamicComponent blok={item} key={item._uid} canTransitionIn={canTransitionIn} />
        ))}
        <CitiesNavigation blok={caseData} />
      </section>
      {((revealNextCase && store.fromPath?.startsWith(StorySlug.Work)) || // case coming from work
        store.fromPath?.startsWith(StorySlug.Cases) || // case coming from cases (eg: when landing on next case)
        ['', '/', '?'].includes(store.fromPath!)) && ( // case coming from home page ; when coming from external fromPath is undefined
        <section>
          {nextStory && (
            <NextCase
              blok={nextStory.content as NextCaseBlokData}
              full_slug={nextStory.full_slug}
              uuid={nextStory.uuid}
              canTransitionIn={canTransitionIn}
            />
          )}
        </section>
      )}
    </>
  );
}
